import React, {forwardRef, Fragment, useEffect, useRef, useState} from 'react';
// import './PromptEditor.scss';

const MOODBOARD_IMAGE_KIND = 'moodboard_image';
const ITEM_KIND = 'item';

const PREDEFINED_PROMPTS = {
  [MOODBOARD_IMAGE_KIND]: [
    'Create a 3D-style visual based on this image and I would like this image to be as daytime. Keep all elements exactly the same as the image.',
    'Replace the coffee table from the image with this exact one and keep everything else exactly the same.',
    'Generate an image with a straight front view of this item.',
  ],
  [ITEM_KIND]: [
    'Remove background',
  ]
};

export default function PromptEditor({ image: defaultImage, mask: defaultMask, moodboardId, initialPrompt, onGenerate, kind, expandSignal, disabled }) {
  const promptRef = useRef();
  const mainInputRef = useRef();
  const maskInputRef = useRef();

  const [mainImage, setMainImage] = useState(defaultImage || null);
  const [maskImage, setMaskImage] = useState(defaultMask || null);
  // const [expanded, setExpanded] = useState(!!defaultImage);

  const [uploadProgress, setUploadProgress] = useState({ main: 0, mask: 0 });
  const [uploading, setUploading] = useState({ main: false, mask: false });
  const [uploadErrors, setUploadErrors] = useState({ main: null, mask: null });

  const getPredefinedPrompts = () => {
    return PREDEFINED_PROMPTS[kind || MOODBOARD_IMAGE_KIND];
  };

  const [prompt, setPrompt] = useState(initialPrompt || getPredefinedPrompts()[0]);
  const [showPresets, setShowPresets] = useState(false);
  const [layout, setLayout] = useState('landscape');
  const [quality, setQuality] = useState('norm');

  const canGenerate = !disabled && mainImage && !uploading.main && !uploading.mask && prompt && prompt.length >= 2;


  const focus = () => {
    promptRef.current && promptRef.current.focus();
  };

  useEffect(() => {
    // setExpanded(true);
    focus();
  }, [expandSignal]);

  useEffect(() => {
      if (initialPrompt) {
        setPrompt(initialPrompt);
        // setExpanded(true);
        focus();
      }
  }, [initialPrompt]);

  useEffect(() => {
      if (defaultImage) {
        setMainImage(defaultImage);
        // setExpanded(true);
        // promptRef.current && promptRef.current.focus();
        focus();
      }
  }, [defaultImage]);

  const handlePresetClick = (preset) => setPrompt(preset);

  const handleGenerateClick = () => {
    if (!canGenerate) return;
    onGenerate(
      prompt,
      mainImage,
      maskImage,
      layout,
      quality,
      // moodboard_id: moodboardId,
    );
    setMaskImage(null);
    setMainImage(null);
    setPrompt('');
  };

  const uploadImage = (file, type) => {
    if (!file) return;

    const isMask = type === 'mask';
    const setImage = isMask ? setMaskImage : setMainImage;
    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    setUploadErrors(prev => ({ ...prev, [type]: null }));

    // Local preview
    const localUrl = URL.createObjectURL(file);
    setImage({ id: null, url: { original: localUrl, thumbnail: localUrl } });

    const formData = new FormData();
    formData.append('ai_image[file]', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/design/ai/requests/create_image');

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded * 100) / e.total);
        setUploadProgress(prev => ({ ...prev, [type]: percent }));
      }
    });

    xhr.onload = () => {
      setUploading(prev => ({ ...prev, [type]: false }));
      try {
        const data = JSON.parse(xhr.responseText);
        if (data.image) {
          // debugger;
          setImage(data.image);
        } else {
          throw new Error('Upload failed');
        }
      } catch (e) {
        setUploadErrors(prev => ({ ...prev, [type]: 'Upload failed' }));
      }
    };

    xhr.onerror = () => {
      setUploading(prev => ({ ...prev, [type]: false }));
      setUploadErrors(prev => ({ ...prev, [type]: 'Upload failed' }));
    };

    xhr.send(formData);
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    // setExpanded(true);
    uploadImage(file, type);
  };

  const handleFileChange = (e, type) => {
    // setExpanded(true);
    uploadImage(e.target.files[0], type);
  };

  const onImageDelete = (mask = false) => {
    mask ? setMaskImage(null) : setMainImage(null);
  };

  const renderUploadPlaceholder = () => {
    return (
      <div className="upload-placeholder">
        <svg className="upload-icon" viewBox="0 0 24 24">
          <path fill="currentColor" d="M5 20h14v-2H5v2zm7-18L5.33 8.67 7.75 11l3.25-3.25V17h2V7.75L16.25 11 18.67 8.67 12 2z"/>
        </svg>
        <span>Drag & drop image<br/> or click to choose</span>
      </div>
    );
  };

  return (
    <div className="prompt-editor">
      <div
        className={`prompt-image-slot ${mainImage ? '-image-selected' : ''}`}
        onDrop={(e) => handleDrop(e, 'main')}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => mainInputRef.current.click()}
      >
        {mainImage ? (
          <div className="image-preview">
            <img src={mainImage.url.thumbnail} alt="Image" />
            <button className="delete-btn" onClick={(e) => {e.preventDefault(); onImageDelete(maskImage)}} />
          </div>
        ) : renderUploadPlaceholder() }
        <input type="file" ref={mainInputRef} className="file-input" onChange={(e) => handleFileChange(e, 'main')} />
        {uploading.main && (
          <div className="progress-bar"><div className="progress" style={{ width: `${uploadProgress.main}%` }} /></div>
        )}
        {uploadErrors.main && <div className="upload-error">{uploadErrors.main}</div>}
      </div>


      <div  className="prompt-input-area">
        <div className="prompt-section">
          <textarea ref={promptRef} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="prompt-textarea" />
          {showPresets && (
            <ul className="preset-list">
              {getPredefinedPrompts().map((p, i) => <li key={i} onClick={() => {setShowPresets(false); handlePresetClick(p)}}>{p}</li>)}
            </ul>
          )}
        </div>
        <div className="prompt-actions">
          <button className="btn -empty -y2023 toggle-presets" onClick={() => setShowPresets(!showPresets)}>
            {showPresets ? 'Hide' : 'Prompts'}
          </button>
          <button className="btn -primary -y2023 generate-button" onClick={handleGenerateClick} disabled={!canGenerate}>Generate</button>
          {false && (
            <div className="options">
              <div className="layout-options">
                {['landscape', 'portrait', 'square'].map(opt => (
                  <label key={opt}>
                    <input type="radio" checked={layout === opt} onChange={() => setLayout(opt)} />
                    <span className="layout-svg">{opt[0].toUpperCase()}</span>
                  </label>
                ))}
              </div>
              <div className="quality-options">
                {['low', 'norm', 'high'].map(opt => (
                  <label key={opt}>
                    <input type="radio" checked={quality === opt} onChange={() => setQuality(opt)} />
                    <span className="layout-svg">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/moodboard_ai_requests/PromptEditor.js